import { useEffect, useState } from "react";

import type { RangeParams, SeriesResponse } from "./api.js";

/**
 * Fetches a series for the given range and refetches when the range changes.
 * On refetch the previous points are kept until the new ones land, so the
 * chart holds its last render instead of flashing to empty.
 */
export function useSeries<T>(
  fetcher: (range: RangeParams) => Promise<SeriesResponse<T>>,
  hours: number,
  deps: unknown[] = [],
): { data: SeriesResponse<T> | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<SeriesResponse<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - hours * 3_600_000);

    fetcher({ from, to })
      .then((response) => {
        if (!cancelled) {
          setData(response);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, ...deps]);

  return { data, loading, error };
}
